import React, { Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Inbox, CheckCircle2, Clock, ChevronRight, Search } from 'lucide-react';
const Spline = lazy(() => import('@splinetool/react-spline'));
export function HeroSection() {
  const headlineWords = 'Book live shows without living in spreadsheets and inbox chaos.'.split(' ');
  return <section className="relative pt-40 pb-20 overflow-hidden min-h-screen flex flex-col justify-center" data-id="element-359">
      {/* Spline 3D Background */}
      <div className="absolute inset-0 z-0" data-id="element-360">
        <Suspense fallback={<div className="absolute inset-0 bg-background" data-id="element-362" />} data-id="element-361">
          <Spline scene="https://prod.spline.design/5r8YDEt51WGzoWWc/scene.splinecode" style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0
        }} data-id="element-363" />
        </Suspense>
        {/* Gradient overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background/90 pointer-events-none" data-id="element-364" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" data-id="element-365" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-366">
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-367">
          <motion.h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.1]" data-id="element-368">
            {headlineWords.map((word, i) => {
            const isHighlight = word === 'spreadsheets' || word.includes('chaos');
            const isGradient = i < 3;
            return <motion.span key={i} initial={{
              opacity: 0,
              y: 20,
              filter: 'blur(10px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} className={`inline-block mr-3 ${isHighlight ? 'text-textMuted' : ''} ${isGradient ? 'gradient-text' : ''}`} data-id="element-369">
                  {word}
                </motion.span>;
          })}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-lg md:text-xl text-textMuted mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-370">
            Booker helps artists plan tours, venues fill calendars, and agents
            run their roster — all in one workflow for discovery, outreach,
            negotiation, contracts, deposits, lineup locking, and show ops.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-371">
            <button className="group w-full sm:w-auto px-8 py-4 text-base font-medium text-background bg-primary rounded-lg hover:bg-primaryHover transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.6)] hover:-translate-y-1 flex items-center justify-center gap-2" style={{
            animation: 'pulse-glow 3s infinite'
          }} data-id="element-372">
              Start free
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-373" />
            </button>
            <button className="w-full sm:w-auto px-8 py-4 text-base font-medium text-textMain border border-border rounded-lg hover:bg-surface transition-colors flex items-center justify-center hover:-translate-y-1" data-id="element-374">
              Book a demo
            </button>
          </motion.div>
        </div>

        {/* UI Mockup with Glow Border */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.8
      }} className="relative mx-auto max-w-5xl rounded-xl glow-border-wrapper shadow-2xl" style={{
        animation: 'float 6s ease-in-out infinite'
      }} data-id="element-375">
          <div className="glow-border-content flex flex-col" data-id="element-376">
            {/* Mockup Header */}
            <div className="h-12 border-b border-border flex items-center px-4 gap-2 bg-surface rounded-t-[inherit]" data-id="element-377">
              <div className="flex gap-1.5" data-id="element-378">
                <div className="w-3 h-3 rounded-full bg-border hover:bg-red-500 transition-colors cursor-pointer" data-id="element-379"></div>
                <div className="w-3 h-3 rounded-full bg-border hover:bg-yellow-500 transition-colors cursor-pointer" data-id="element-380"></div>
                <div className="w-3 h-3 rounded-full bg-border hover:bg-green-500 transition-colors cursor-pointer" data-id="element-381"></div>
              </div>
              <div className="mx-auto bg-background border border-border rounded-md px-32 py-1 text-xs text-textMuted flex items-center gap-2" data-id="element-382">
                <Search className="w-3 h-3" data-id="element-383" />
                Search venues, artists, or deals...
              </div>
            </div>

            {/* Mockup Body */}
            <div className="grid grid-cols-12 h-[500px] rounded-b-[inherit] overflow-hidden" data-id="element-384">
              {/* Sidebar */}
              <div className="col-span-2 border-r border-border p-4 flex flex-col gap-4 bg-surface/30" data-id="element-385">
                <div className="flex items-center gap-2 text-sm font-medium text-primary bg-primary/10 px-3 py-2 rounded-md" data-id="element-386">
                  <MapPin className="w-4 h-4" data-id="element-387" />
                  Route Planner
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-textMuted hover:text-textMain px-3 py-2 rounded-md transition-colors" data-id="element-388">
                  <Inbox className="w-4 h-4" data-id="element-389" />
                  Inbox
                  <span className="ml-auto bg-primary text-background text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-390">
                    3
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-textMuted hover:text-textMain px-3 py-2 rounded-md transition-colors" data-id="element-391">
                  <Calendar className="w-4 h-4" data-id="element-392" />
                  Deals
                </div>
              </div>

              {/* Main Content - Route Planner */}
              <div className="col-span-7 border-r border-border p-6 relative overflow-hidden bg-[#0A0A0F]" data-id="element-393">
                <div className="flex justify-between items-center mb-6" data-id="element-394">
                  <div data-id="element-395">
                    <h3 className="text-lg font-semibold" data-id="element-396">Summer Run '26</h3>
                    <p className="text-xs text-textMuted" data-id="element-397">
                      Midwest to New England • 14 targets
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-surface border border-border rounded-md text-xs font-medium" data-id="element-398">
                    Drafting
                  </div>
                </div>

                {/* Mock Map Area */}
                <div className="absolute inset-0 top-24 left-6 right-6 bottom-6 border border-border rounded-lg bg-[#0F0F14] overflow-hidden" data-id="element-399">
                  {/* Map Grid Lines */}
                  <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(#27272A 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                  opacity: 0.3
                }} data-id="element-400"></div>

                  {/* Route Nodes */}
                  <div className="absolute top-1/4 left-1/4 w-4 h-4 rounded-full bg-primary shadow-[0_0_15px_rgba(245,158,11,0.8)] z-10 animate-pulse" data-id="element-401">
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium bg-surface px-2 py-1 rounded border border-border" data-id="element-402">
                      Chicago, IL
                    </div>
                  </div>
                  <div className="absolute top-1/3 left-1/2 w-4 h-4 rounded-full bg-primary shadow-[0_0_15px_rgba(245,158,11,0.8)] z-10 animate-pulse" style={{
                  animationDelay: '0.5s'
                }} data-id="element-403">
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium bg-surface px-2 py-1 rounded border border-border" data-id="element-404">
                      Cleveland, OH
                    </div>
                  </div>
                  <div className="absolute top-1/4 right-1/4 w-4 h-4 rounded-full bg-border border-2 border-primary z-10" data-id="element-405">
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium bg-surface px-2 py-1 rounded border border-border text-primary" data-id="element-406">
                      Buffalo, NY (Target)
                    </div>
                  </div>

                  {/* Route Lines */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-407">
                    <motion.path initial={{
                    pathLength: 0
                  }} animate={{
                    pathLength: 1
                  }} transition={{
                    duration: 2,
                    delay: 1.5,
                    ease: 'easeInOut'
                  }} d="M 25% 25% L 50% 33% L 75% 25%" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 4" className="opacity-50" data-id="element-408" />
                  </svg>
                </div>
              </div>

              {/* Right Sidebar - Deals Snippet */}
              <div className="col-span-3 p-4 bg-surface/20 flex flex-col gap-4" data-id="element-409">
                <h4 className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-2" data-id="element-410">
                  Active Deals
                </h4>

                <motion.div whileHover={{
                scale: 1.02
              }} className="bg-surface border border-border rounded-lg p-3 shadow-sm cursor-pointer transition-colors hover:border-primary/50" data-id="element-411">
                  <div className="flex justify-between items-start mb-2" data-id="element-412">
                    <span className="text-sm font-medium" data-id="element-413">
                      The Empty Bottle
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 text-green-500 rounded" data-id="element-414">
                      Offer
                    </span>
                  </div>
                  <p className="text-xs text-textMuted mb-3" data-id="element-415">
                    Jun 12 • $1,500 Guar.
                  </p>
                  <div className="flex items-center gap-2 text-[10px] text-textMuted" data-id="element-416">
                    <Clock className="w-3 h-3" data-id="element-417" />
                    Awaiting response
                  </div>
                </motion.div>

                <motion.div whileHover={{
                scale: 1.02
              }} className="bg-surface border border-border rounded-lg p-3 shadow-sm cursor-pointer transition-colors hover:border-primary/50" data-id="element-418">
                  <div className="flex justify-between items-start mb-2" data-id="element-419">
                    <span className="text-sm font-medium" data-id="element-420">
                      Beachland Tavern
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded" data-id="element-421">
                      Hold 1
                    </span>
                  </div>
                  <p className="text-xs text-textMuted mb-3" data-id="element-422">Jun 14 • TBD</p>
                  <div className="flex items-center gap-2 text-[10px] text-textMuted" data-id="element-423">
                    <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-424" />
                    Lineup confirmed
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}